import { useState, useEffect, useCallback } from 'react';
import { url } from '../../../api';
import { apiClient } from '../../../api/client';
import { getAuthToken } from '../../../utils/authTokens';
import { coffeeShopPaths } from '../../coffee_card_web/api/paths';

export interface ClusterShop {
    _id: string;
    name: string;
    place_id?: string;
    coordinates: { lat: number; lng: number };
    address: string;
    city: string;
    country: string;
    shopType: string;
    status: string;
    chainId: string | null;
    chainName: string | null;
    totalRatings: number;
    averageRating: number;
    coffeeTypeStats?: Record<string, { count: number; averageRating: number; lastRated: string }>;
    googleRating?: number;
    priceLevel?: number;
    types: string[];
    isActive: boolean;
}

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getAuthToken()}` } });

export function useClusterShops(clusterId: string | null, includeArchived = false) {
    const [shops, setShops] = useState<ClusterShop[]>([]);
    const [loading, setLoading] = useState(!!clusterId);
    const [error, setError] = useState<string | null>(null);

    const fetchShops = useCallback(async () => {
        if (!clusterId) {
            setShops([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const params = includeArchived ? { includeArchived: 'true' } : {};
            const res = await apiClient.get(`${url}${coffeeShopPaths.byCluster(clusterId)}`, {
                ...authHeaders(),
                params
            });
            setShops(res.data?.coffeeShops || []);
        } catch (err: any) {
            const raw = err?.response?.data?.error;
            const msg = typeof raw === 'object' && raw?.message ? raw.message : (typeof raw === 'string' ? raw : err?.response?.data?.message || 'Failed to fetch shops');
            setError(msg);
            setShops([]);
        } finally {
            setLoading(false);
        }
    }, [clusterId, includeArchived]);

    useEffect(() => {
        fetchShops();
    }, [fetchShops]);

    return { shops, loading, error, refetch: fetchShops };
}
